import * as React from 'react';
import { Drawer, Tabs, Table, Tag } from 'antd';
import { CALCULATION_DETAILS_COLUMNS } from '../Receivables.utils';
import { formatDateTime, capitalize } from 'src/utils/utils';
import EventsTimeline from './EventsTimeline';
import InputParameters from './InputParameters';
import { fetchPayoutEvents, clearPayoutEvents } from '../Receivables.actionsCreator';
import Events from './Events';
import PayoutDetailsTable from './PayoutDetailsTable';
import GenericHoc from 'src/common/generic-hoc';
import { HocOptions } from 'src/common/generic-hoc.types';
import { CalculationDetailsDrawerStyles } from './CalculationDetailsDrawer.styles';

const { TabPane } = Tabs;

interface CalculationDetailsDrawerProps {
  visible: boolean;
  onClose: () => void;
  calculationData: {
    id?: string;
    computation?: Record<string, any>;
    payout?: Array<{
      charge_id?: string;
      calculated_payout?: number;
      payout?: number;
      remarks?: string;
    }>;
    object_type_name?: string;
    start_date?: string;
    end_date?: string;
    hub_code?: string;
    entity_type?: string;
    entity_reference_number?: string;
    status?: string;
    [key: string]: any;
  };
  isGroupedView?: boolean;
  payoutEvents: {
    data: any[];
    loading: boolean;
    error: string | null;
  };
  computationHeaders: any[];
  fetchPayoutEventsAction: (payoutId: string) => void;
  clearPayoutEventsAction: () => void;
  t: (key: string) => string;
  classes: Record<string, string>;
}

const CalculationDetailsDrawer: React.FC<CalculationDetailsDrawerProps> = ({
  visible,
  onClose,
  calculationData,
  isGroupedView,
  payoutEvents,
  computationHeaders,
  fetchPayoutEventsAction,
  clearPayoutEventsAction,
  t,
  classes,
}) => {
  const [activeTab, setActiveTab] = React.useState<string>('charge_breakup');
  const [tableKey, setTableKey] = React.useState<number>(0);

  const payoutId = calculationData?.id || '';

  React.useEffect(() => {
    if (visible) {
      if (payoutId && !isGroupedView) {
        fetchPayoutEventsAction(payoutId);
      }
      setTableKey((prev) => prev + 1);
    }

    return () => {
      if (!visible) {
        clearPayoutEventsAction();
        setActiveTab('charge_breakup');
      }
    };
  }, [visible, payoutId, isGroupedView, fetchPayoutEventsAction, clearPayoutEventsAction]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (key === 'events' && payoutId) {
      fetchPayoutEventsAction(payoutId);
    }
  };

  const getCalculationColumns = () => {
    return CALCULATION_DETAILS_COLUMNS.map((column) => ({
      ...column,
      title: t(column.title),
      align: column.align as any,
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      case 'pending':
      case 'sent_for_review':
        return 'orange';
      case 'open':
        return 'blue';
      default:
        return 'default';
    }
  };

  const renderMetadataSection = () => {
    return (
      <div className={classes.headerContainer}>
        <div className={classes.gridContainer}>
          <div className={classes.item}>
            <div className={classes.label}>{t('Billing cycle')}:</div>
            <div className={classes.value}>
              {calculationData.start_date && calculationData.end_date
                ? `${formatDateTime(calculationData.start_date, 'DD MMM YYYY')} - ${formatDateTime(calculationData.end_date, 'DD MMM YYYY')}`
                : '-'}
            </div>
          </div>
          <div className={classes.item}>
            <div className={classes.label}>{t('Hub Code')}:</div>
            <div className={classes.value}>{calculationData.hub_code || '-'}</div>
          </div>
          <div className={classes.item}>
            <div className={classes.label}>{t('Entity type')}:</div>
            <div className={classes.value}>{capitalize(calculationData.entity_type || '-')}</div>
          </div>
          <div className={classes.item}>
            <div className={classes.label}>{t('Entity Reference Number')}:</div>
            <div className={classes.value}>{calculationData.entity_reference_number || '-'}</div>
          </div>
          <div className={classes.item}>
            <div className={classes.label}>{t('Status')}:</div>
            <Tag color={getStatusColor((calculationData.status || '').toLowerCase())}>
              {capitalize(calculationData.status || '-')}
            </Tag>
          </div>
        </div>
      </div>
    );
  };

  const renderSummary = (pageData) => {
    const updatedTotal = pageData.reduce(
      (acc, curr) => acc + (parseFloat(curr?.payout) || 0),
      0,
    );

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0}>
          <strong>{t('Total Amount')}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1} align="right">
          <strong>{updatedTotal}</strong>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  return (
    <Drawer
      title={t('Calculation Details')}
      placement="right"
      onClose={onClose}
      visible={visible}
      width="90%"
      bodyStyle={{ padding: 0 }}
    >
      {renderMetadataSection()}

      <div className={isGroupedView ? classes.contentContainer : classes.bodyContainer}>
        {!isGroupedView && (
          <EventsTimeline
            events={payoutEvents?.data || []}
            loading={payoutEvents?.loading || false}
            error={payoutEvents?.error || null}
          />
        )}

        <div className={classes.contentContainer}>
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            className={classes.tabs}
            tabBarStyle={{ padding: '0 20px', margin: 0 }}
            destroyInactiveTabPane
          >
            <TabPane tab={t('Charge Breakup')} key="charge_breakup">
              <div className={classes.tabsContainer}>
                {isGroupedView ? (
                  <PayoutDetailsTable
                    key={tableKey}
                    calculationData={calculationData}
                  />
                ) : (
                  <Table
                    dataSource={calculationData?.payout || []}
                    columns={getCalculationColumns()}
                    pagination={false}
                    size="small"
                    bordered
                    summary={renderSummary}
                  />
                )}
              </div>
            </TabPane>
            <TabPane tab={t('Input Parameters')} key="input_parameters">
              <div className={classes.tabsContainer}>
                <InputParameters
                  data={calculationData?.computation || {}}
                  computationHeaders={computationHeaders}
                />
              </div>
            </TabPane>
            {isGroupedView && (
              <TabPane tab={t('Events')} key="events">
                <div className={classes.tabsContainerNoPadding}>
                  <Events
                    events={payoutEvents?.data || []}
                    loading={payoutEvents?.loading || false}
                  />
                </div>
              </TabPane>
            )}
          </Tabs>
        </div>
      </div>
    </Drawer>
  );
};

const mapStateToProps = (state: any) => ({
  payoutEvents: state.receivables?.payoutEvents || { data: [], loading: false, error: null },
  computationHeaders: state.receivables?.computationHeadersToShow || [],
  isGroupedView: state.receivables?.isGroupedView || false,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchPayoutEventsAction: (payoutId: string) => dispatch(fetchPayoutEvents(payoutId)),
  clearPayoutEventsAction: () => dispatch(clearPayoutEvents()),
});

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
    mapDispatchToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: CalculationDetailsDrawerStyles,
  },
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(CalculationDetailsDrawer);
